import * as Sentry from "@sentry/react"
// import { QueryClient, useQueryClient } from "@tanstack/react-query"
// import { useMemo } from "react"
import { Navigate, RouterProvider, createBrowserRouter } from "react-router-dom"

import { ProtectedRoute } from "@/lib/auth"
import { ModuleGuard } from "@/lib/module-guard"

import { AppRoot } from "./routes/app/root"
import ErrorPage from "./routes/error"

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter)

// const createRouter = (queryClient: QueryClient) =>
const createRouter = () =>
  sentryCreateBrowserRouter(
    [
      {
        path: "/",
        element: <Navigate to="/app" replace />,
      },
      {
        path: "/auth/login",
        errorElement: <ErrorPage />,
        lazy: async () => {
          const { LoginRoute } = await import("./routes/auth/login")
          return { Component: LoginRoute }
        },
      },
      {
        path: "/app",
        errorElement: <ErrorPage />,
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <AppRoot />
            </ModuleGuard>
          </ProtectedRoute>
        ),
        children: [
          {
            errorElement: <ErrorPage />,
            children: [
              {
                path: "",
                lazy: async () => {
                  const { DashboardRoute } = await import(
                    "./routes/app/dashboard"
                  )
                  return { Component: DashboardRoute }
                },
              },
              {
                path: "export",
                lazy: async () => {
                  const { ExportDataRoute } = await import(
                    "./routes/app/export-data"
                  )
                  return { Component: ExportDataRoute }
                },
              },
              // {
              //   path: "permissions",
              //   lazy: async () => {
              //     const { PermissionsRoute } = await import(
              //       "./routes/app/permissions"
              //     )
              //     return { Component: PermissionsRoute }
              //   },
              //   loader: async () => {
              //     const { permissionsLoader } = await import(
              //       "./routes/app/permissions"
              //     )
              //     return permissionsLoader(queryClient)()
              //   },
              // },
              {
                path: "patrol",
                lazy: async () => {
                  const { PatrolRoute } = await import(
                    "./routes/app/patrol/patrol"
                  )
                  return { Component: PatrolRoute }
                },
              },
              {
                path: "patrol/detail-patrol-activity",
                lazy: async () => {
                  const { DetailPatrolActivityRoute } = await import(
                    "./routes/app/patrol/detail-patrol-activity"
                  )
                  return { Component: DetailPatrolActivityRoute }
                },
              },
              {
                path: "patrol/export",
                lazy: async () => {
                  const { ExportRoute } = await import("./routes/app/export")
                  return { Component: ExportRoute }
                },
              },
              {
                path: "patrol/area-and-assets",
                lazy: async () => {
                  const { AreaAndAssetsRoute } = await import(
                    "./routes/app/area-and-assets/area-and-assets"
                  )
                  return { Component: AreaAndAssetsRoute }
                },
              },
              {
                path: "patrol/area-and-assets/edit-detail-asset",
                lazy: async () => {
                  const { EditDetailAssetRoute } = await import(
                    "./routes/app/area-and-assets/edit-detail-asset"
                  )
                  return { Component: EditDetailAssetRoute }
                },
              },
              {
                path: "attendance",
                lazy: async () => {
                  const { AttendanceRoute } = await import(
                    "./routes/app/attendance"
                  )
                  return { Component: AttendanceRoute }
                },
              },
              {
                path: "request",
                lazy: async () => {
                  const { RequestRoute } = await import(
                    "./routes/app/request/request"
                  )
                  return { Component: RequestRoute }
                },
              },
              {
                path: "request/edit-list-approval",
                lazy: async () => {
                  const { UpdateListApprovalRoute } = await import(
                    "./routes/app/request/list-approval/update-list-approval"
                  )
                  return { Component: UpdateListApprovalRoute }
                },
              },
              {
                path: "request/detail-request",
                lazy: async () => {
                  const { DetailRequestRoute } = await import(
                    "./routes/app/request/detail-request"
                  )
                  return { Component: DetailRequestRoute }
                },
              },
              {
                path: "panic-button",
                lazy: async () => {
                  const { PanicButtonRoute } = await import(
                    "./routes/app/panic-button/panic-button"
                  )
                  return { Component: PanicButtonRoute }
                },
              },
              {
                path: "panic-button/add",
                lazy: async () => {
                  const { AddPanicButtonRoute } = await import(
                    "./routes/app/panic-button/add-panic-button"
                  )
                  return { Component: AddPanicButtonRoute }
                },
              },
              {
                path: "panic-button/detail-panic-button",
                lazy: async () => {
                  const { DetailPanicButtonRoute } = await import(
                    "./routes/app/panic-button/detail-panic-button"
                  )
                  return { Component: DetailPanicButtonRoute }
                },
              },
              {
                path: "building",
                lazy: async () => {
                  const { BuildingRoute } = await import(
                    "./routes/app/building/building"
                  )
                  return { Component: BuildingRoute }
                },
              },
              {
                path: "building/detail-building",
                lazy: async () => {
                  const { DetailBuildingRoute } = await import(
                    "./routes/app/building/detail-building"
                  )
                  return { Component: DetailBuildingRoute }
                },
              },
              {
                path: "building/detail-building/detail-bq",
                lazy: async () => {
                  const { DetailBqRoute } = await import(
                    "./routes/app/building/bq/detail-bq"
                  )
                  return { Component: DetailBqRoute }
                },
              },
              {
                path: "building/detail-building/detail-bq/edit-bq",
                lazy: async () => {
                  const { UpdateBqRoute } = await import(
                    "./routes/app/building/bq/update-bq"
                  )
                  return { Component: UpdateBqRoute }
                },
              },
              {
                path: "building/detail-building/add-building-position",
                lazy: async () => {
                  const { AddBuildingPositionRoute } = await import(
                    "./routes/app/building/building-position/add-building-position"
                  )
                  return { Component: AddBuildingPositionRoute }
                },
              },
              {
                path: "building/detail-building/update-building-position",
                lazy: async () => {
                  const { UpdateBuildingPositionRoute } = await import(
                    "./routes/app/building/building-position/update-building-position"
                  )
                  return { Component: UpdateBuildingPositionRoute }
                },
              },
              {
                path: "building/detail-building/add-bq",
                lazy: async () => {
                  const { AddBQRoute } = await import(
                    "./routes/app/building/bq/add-bq"
                  )
                  return { Component: AddBQRoute }
                },
              },
              // {
              //   path: "building/detail-building/update-building-position",
              //   lazy: async () => {
              //     const { UpdateBuildingPositionRoute } = await import(
              //       "./routes/app/building/building-position/update-building-position"
              //     )
              //     return { Component: UpdateBuildingPositionRoute }
              //   },
              // },
              {
                path: "building/company",
                lazy: async () => {
                  const { CompanyRoute } = await import(
                    "./routes/app/building/company"
                  )
                  return { Component: CompanyRoute }
                },
              },
              {
                path: "applicant",
                lazy: async () => {
                  const { ApplicantRoute } = await import(
                    "./routes/app/human-resource/applicant/applicant"
                  )
                  return { Component: ApplicantRoute }
                },
              },
              {
                path: "applicant/add",
                lazy: async () => {
                  const { AddApplicantRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/add-applicant"
                  )
                  return { Component: AddApplicantRoute }
                },
              },
              {
                path: "applicant/detail-applicant",
                lazy: async () => {
                  const { DetailApplicantRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant"
                  )
                  return { Component: DetailApplicantRoute }
                },
              },
              {
                path: "applicant/detail-applicant/update-personel",
                lazy: async () => {
                  const { UpdatePersonalInformationRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/update-personal-information"
                  )
                  return { Component: UpdatePersonalInformationRoute }
                },
              },
              {
                path: "applicant/detail-applicant/add-education-and-experience",
                lazy: async () => {
                  const { AddEducationAndExperienceRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/add-education-and-experience"
                  )
                  return { Component: AddEducationAndExperienceRoute }
                },
              },
              {
                path: "applicant/detail-applicant/update-education",
                lazy: async () => {
                  const { UpdateEducationRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/update-education"
                  )
                  return { Component: UpdateEducationRoute }
                },
              },
              {
                path: "applicant/detail-applicant/update-experience",
                lazy: async () => {
                  const { UpdateExperienceRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/update-experience"
                  )
                  return { Component: UpdateExperienceRoute }
                },
              },
              {
                path: "applicant/detail-applicant/add-certificate",
                lazy: async () => {
                  const { AddCertificateRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/add-certificate"
                  )
                  return { Component: AddCertificateRoute }
                },
              },
              {
                path: "applicant/detail-applicant/update-certificate",
                lazy: async () => {
                  const { UpdateCertificateRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/update-certificate"
                  )
                  return { Component: UpdateCertificateRoute }
                },
              },
              {
                path: "applicant/detail-applicant/add-bank",
                lazy: async () => {
                  const { AddBankRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/add-bank"
                  )
                  return { Component: AddBankRoute }
                },
              },
              {
                path: "applicant/detail-applicant/update-bank",
                lazy: async () => {
                  const { UpdateBankRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/update-bank"
                  )
                  return { Component: UpdateBankRoute }
                },
              },
              {
                path: "applicant/detail-applicant/add-family",
                lazy: async () => {
                  const { AddFamilyRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/add-family"
                  )
                  return { Component: AddFamilyRoute }
                },
              },
              {
                path: "applicant/detail-applicant/update-family",
                lazy: async () => {
                  const { UpdateFamilyRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/update-family"
                  )
                  return { Component: UpdateFamilyRoute }
                },
              },
              {
                path: "applicant/detail-applicant/add-bpjs",
                lazy: async () => {
                  const { AddBpjsRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/add-bpjs"
                  )
                  return { Component: AddBpjsRoute }
                },
              },
              {
                path: "applicant/detail-applicant/update-bpjs",
                lazy: async () => {
                  const { UpdateBpjsRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/update-bpjs"
                  )
                  return { Component: UpdateBpjsRoute }
                },
              },
              {
                path: "applicant/detail-applicant/add-ijazah",
                lazy: async () => {
                  const { AddIjazahRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/add-ijazah"
                  )
                  return { Component: AddIjazahRoute }
                },
              },
              {
                path: "applicant/detail-applicant/update-ijazah",
                lazy: async () => {
                  const { UpdateIjazahRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/update-ijazah"
                  )
                  return { Component: UpdateIjazahRoute }
                },
              },
              {
                path: "applicant/detail-applicant/add-company-building",
                lazy: async () => {
                  const { AddCompanyBuildingRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/add-company-building"
                  )
                  return { Component: AddCompanyBuildingRoute }
                },
              },
              {
                path: "applicant/detail-applicant/update-company-building",
                lazy: async () => {
                  const { UpdateCompanyBuildingRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/update-company-building"
                  )
                  return { Component: UpdateCompanyBuildingRoute }
                },
              },
              {
                path: "applicant/detail-applicant/assign-account",
                lazy: async () => {
                  const { AssignAccountSecurityRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/assign-account-security"
                  )
                  return { Component: AssignAccountSecurityRoute }
                },
              },
              {
                path: "applicant/detail-applicant/update-assign-account",
                lazy: async () => {
                  const { UpdateAccountSecurityRoute } = await import(
                    "./routes/app/human-resource/applicant/detail-applicant/update-account-security"
                  )
                  return { Component: UpdateAccountSecurityRoute }
                },
              },
              {
                path: "schedule",
                lazy: async () => {
                  const { ScheduleRoute } = await import(
                    "./routes/app/human-resource/schedule/schedule"
                  )
                  return { Component: ScheduleRoute }
                },
              },
              {
                path: "schedule/add-group-schedule",
                lazy: async () => {
                  const { AddGroupScheduleRoute } = await import(
                    "./routes/app/human-resource/schedule/group/add-group-schedule"
                  )
                  return { Component: AddGroupScheduleRoute }
                },
              },
              {
                path: "schedule/update-group-schedule",
                lazy: async () => {
                  const { UpdateGroupScheduleRoute } = await import(
                    "./routes/app/human-resource/schedule/group/update-group-schedule"
                  )
                  return { Component: UpdateGroupScheduleRoute }
                },
              },
              {
                path: "schedule/detail-attendance",
                lazy: async () => {
                  const { DetailAttendanceRoute } = await import(
                    "./routes/app/human-resource/schedule/detail-attendance"
                  )
                  return { Component: DetailAttendanceRoute }
                },
              },
              {
                path: "schedule/add-shift-schedule",
                lazy: async () => {
                  const { AddShiftScheduleRoute } = await import(
                    "./routes/app/human-resource/schedule/shift/add-shift-schedule"
                  )
                  return { Component: AddShiftScheduleRoute }
                },
              },
              {
                path: "schedule/edit-shift-schedule",
                lazy: async () => {
                  const { EditShiftScheduleRoute } = await import(
                    "./routes/app/human-resource/schedule/shift/edit-shift-schedule"
                  )
                  return { Component: EditShiftScheduleRoute }
                },
              },
              {
                path: "schedule/add",
                lazy: async () => {
                  const { AddScheduleRoute } = await import(
                    "./routes/app/human-resource/schedule/add-schedule"
                  )
                  return { Component: AddScheduleRoute }
                },
              },
              {
                path: "schedule/edit",
                lazy: async () => {
                  const { EditScheduleRoute } = await import(
                    "./routes/app/human-resource/schedule/edit-schedule"
                  )
                  return { Component: EditScheduleRoute }
                },
              },
              {
                path: "payroll",
                lazy: async () => {
                  const { PayrollRoute } = await import(
                    "./routes/app/human-resource/payroll/payroll"
                  )
                  return { Component: PayrollRoute }
                },
              },
              {
                path: "payroll/detail-payroll",
                lazy: async () => {
                  const { DetailPayrollRoute } = await import(
                    "./routes/app/human-resource/payroll/detail-payroll"
                  )
                  return { Component: DetailPayrollRoute }
                },
              },
              {
                path: "payroll/create-deduction",
                lazy: async () => {
                  const { AddDeductionRoute } = await import(
                    "./routes/app/human-resource/payroll/deduction/add-deduction"
                  )
                  return { Component: AddDeductionRoute }
                },
              },
              {
                path: "payroll/edit-deduction",
                lazy: async () => {
                  const { EditDeductionRoute } = await import(
                    "./routes/app/human-resource/payroll/deduction/edit-deduction"
                  )
                  return { Component: EditDeductionRoute }
                },
              },
              {
                path: "payroll/create-im",
                lazy: async () => {
                  const { CreateImRoute } = await import(
                    "./routes/app/human-resource/payroll/im/create-im"
                  )
                  return { Component: CreateImRoute }
                },
              },
              {
                path: "payroll/detail-im",
                lazy: async () => {
                  const { DetailImRoute } = await import(
                    "./routes/app/human-resource/payroll/im/detail-im"
                  )
                  return { Component: DetailImRoute }
                },
              },
              {
                path: "payroll/edit-im",
                lazy: async () => {
                  const { EditImRoute } = await import(
                    "./routes/app/human-resource/payroll/im/edit-im"
                  )
                  return { Component: EditImRoute }
                },
              },
              // {
              //   path: "logistic",
              //   lazy: async () => {
              //     const { LogisticRoute } = await import(
              //       "./routes/app/logistic"
              //     )
              //     return { Component: LogisticRoute }
              //   },
              // },
              // {
              //   path: "finance",
              //   lazy: async () => {
              //     const { FinanceRoute } = await import("./routes/app/finance")
              //     return { Component: FinanceRoute }
              //   },
              // },
              {
                path: "user",
                lazy: async () => {
                  const { UserRoute } = await import(
                    "./routes/app/user-management/user"
                  )
                  return { Component: UserRoute }
                },
              },
              {
                path: "role",
                lazy: async () => {
                  const { RoleRoute } = await import(
                    "./routes/app/user-management/role"
                  )
                  return { Component: RoleRoute }
                },
              },
              {
                path: "role/add",
                lazy: async () => {
                  const { AddRoleRoute } = await import(
                    "./routes/app/user-management/add-role"
                  )
                  return { Component: AddRoleRoute }
                },
              },
              {
                path: "role/edit",
                lazy: async () => {
                  const { EditRoleRoute } = await import(
                    "./routes/app/user-management/edit-role"
                  )
                  return { Component: EditRoleRoute }
                },
              },
              {
                path: "app-setting",
                lazy: async () => {
                  const { AppSettingRoute } = await import(
                    "./routes/app/app-setting"
                  )
                  return { Component: AppSettingRoute }
                },
              },
              {
                path: "bq-master-item",
                lazy: async () => {
                  const { BQMasterItemRoute } = await import(
                    "./routes/app/bq-master-item/bq-master-item"
                  )
                  return { Component: BQMasterItemRoute }
                },
              },
              {
                path: "bq-master-item/create-bq-master-item",
                lazy: async () => {
                  const { AddBQMasterItemRoute } = await import(
                    "./routes/app/bq-master-item/add-bq-master-item"
                  )
                  return { Component: AddBQMasterItemRoute }
                },
              },
              {
                path: "bq-master-item/update-bq-master-item",
                lazy: async () => {
                  const { EditBQMasterItemRoute } = await import(
                    "./routes/app/bq-master-item/edit-bq-master-item"
                  )
                  return { Component: EditBQMasterItemRoute }
                },
              },
            ],
          },
        ],
      },
      {
        path: "*",
        lazy: async () => {
          const { NotFoundRoute } = await import("./routes/not-found-error")
          return { Component: NotFoundRoute }
        },
      },
    ],
    {
      basename: "/",
    }
  )

export const AppRouter = () => {
  // const queryClient = useQueryClient()

  // const router = useMemo(() => createRouter(queryClient), [queryClient])
  const router = createRouter()

  return <RouterProvider router={router} />
}
